import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import api from '../../../../services/api';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    confirmed: { label: 'Confirmado', color: 'success' },
    cancelled: { label: 'Cancelado', color: 'error' }
};

const FULFILLMENT_STATUS_LABEL = {
    reserved: { label: 'Estoque reservado', color: 'default' },
    picking: { label: 'Em separação', color: 'warning' },
    shipped: { label: 'Expedido', color: 'success' }
};

const INVOICE_STATUS_LABEL = {
    pending: { label: 'Fatura pendente de emissão', color: 'warning' },
    issued: { label: 'Fatura emitida', color: 'success' },
    cancelled: { label: 'Fatura cancelada', color: 'error' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateTimeBR = (value) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

const ViewSalesOrder = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [order, setOrder] = useState(null);
    const [shipments, setShipments] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [working, setWorking] = useState(false);

    const [shipDialogOpen, setShipDialogOpen] = useState(false);
    const [carrier, setCarrier] = useState('');
    const [trackingCode, setTrackingCode] = useState('');

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        // Em transições de rota (ex: saindo de "/dashboard/sales/new" ou de
        // "/dashboard/sales/quotes" - rota irmã adicionada junto com
        // Orçamentos), essa tela pode remontar brevemente com `id` ainda
        // não resolvido pro UUID real - sem essa guarda, dispara um GET
        // inválido (".../sales_orders/new/" ou ".../sales_orders/quotes/")
        // que sempre dá erro (o id não é um UUID válido).
        if (!appId || !id || id === 'new' || id === 'quotes' || id === 'price-tables') return;
        carregarPedido();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const carregarPedido = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/sales_orders/${id}/`);
            setOrder(res.data);
            if (res.data.fulfillment_status) {
                const shipRes = await api.get(`v1/apps/${appId}/sales_orders/${id}/shipments/`);
                setShipments(shipRes.data.shipments || []);
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar pedido' });
        }
    };

    const handleConfirm = async () => {
        if (!window.confirm('Confirmar este pedido? Isso vai reservar o estoque e gerar uma conta a receber.')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/sales_orders/${id}/confirm/`, {});
            setOrder(res.data);
            setStatus({ type: 'success', message: 'Pedido confirmado com sucesso!' });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao confirmar pedido' });
        } finally {
            setWorking(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Cancelar este pedido?')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/sales_orders/${id}/cancel/`, {});
            setOrder(res.data);
            setStatus({ type: 'success', message: 'Pedido cancelado.' });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao cancelar pedido' });
        } finally {
            setWorking(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Excluir este pedido em rascunho? Essa ação não pode ser desfeita.')) return;
        setWorking(true);
        try {
            await api.delete(`v1/apps/${appId}/sales_orders/${id}/`);
            history.push('/dashboard/sales');
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir pedido' });
            setWorking(false);
        }
    };

    const handleStartPicking = async () => {
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/sales_orders/${id}/start-picking/`, {});
            setOrder(res.data);
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao iniciar separação' });
        } finally {
            setWorking(false);
        }
    };

    const handleShip = async () => {
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            await api.post(`v1/apps/${appId}/sales_orders/${id}/ship/`, { carrier, tracking_code: trackingCode });
            setShipDialogOpen(false);
            setStatus({ type: 'success', message: 'Pedido expedido com sucesso!' });
            await carregarPedido();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao expedir pedido' });
        } finally {
            setWorking(false);
        }
    };

    const handleInvoice = async () => {
        if (!window.confirm('Faturar este pedido? Isso cria o registro de nota fiscal pendente de emissão.')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            await api.post(`v1/apps/${appId}/invoices/`, { sales_order: id });
            setStatus({ type: 'success', message: 'Pedido faturado - nota fiscal pendente de emissão.' });
            await carregarPedido();
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao faturar pedido' });
        } finally {
            setWorking(false);
        }
    };

    if (!order) {
        return (
            <Box>
                {status.message && <Alert severity={status.type}>{status.message}</Alert>}
            </Box>
        );
    }

    const statusInfo = STATUS_LABEL[order.status] || { label: order.status, color: 'default' };
    const fulfillmentInfo = order.fulfillment_status ? FULFILLMENT_STATUS_LABEL[order.fulfillment_status] : null;
    const invoiceInfo = order.invoice ? INVOICE_STATUS_LABEL[order.invoice.status] : null;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3">Pedido de Venda</Typography>
                <Stack direction="row" spacing={1}>
                    <Chip label={statusInfo.label} color={statusInfo.color} />
                    {fulfillmentInfo && <Chip label={fulfillmentInfo.label} color={fulfillmentInfo.color} variant="outlined" />}
                    {invoiceInfo && <Chip label={invoiceInfo.label} color={invoiceInfo.color} variant="outlined" />}
                </Stack>
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Organização
                            </Typography>
                            <Typography>{order.organization_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Data do pedido
                            </Typography>
                            <Typography>{order.order_date}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Depósito
                            </Typography>
                            <Typography>{order.warehouse_name || 'Depósito principal (padrão)'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Condição de pagamento
                            </Typography>
                            <Typography>{order.payment_term_name || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Desconto
                            </Typography>
                            <Typography>
                                {Number(order.discount_percent) > 0 && `${order.discount_percent}% `}
                                {Number(order.discount_amount) > 0 && formatCurrencyBR(order.discount_amount)}
                                {Number(order.discount_percent) === 0 && Number(order.discount_amount) === 0 && '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Conta a receber gerada
                            </Typography>
                            {order.accounts_receivable ? (
                                <RouterLink to={`/dashboard/financial/receivable/${order.accounts_receivable}/edit`}>
                                    Ver conta a receber
                                </RouterLink>
                            ) : (
                                <Typography>-</Typography>
                            )}
                        </Grid>
                        {order.quote && (
                            <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="text.secondary">
                                    Gerado a partir do orçamento
                                </Typography>
                                <Typography>
                                    <RouterLink to={`/dashboard/sales/quotes/${order.quote}/`}>Ver orçamento</RouterLink>
                                </Typography>
                            </Grid>
                        )}
                        {order.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observações
                                </Typography>
                                <Typography>{order.notes}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Produto</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Valor unitário</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.items?.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.valor_unitario)}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.subtotal)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box textAlign="right" mt={2}>
                        <Typography variant="h5">Total: {formatCurrencyBR(order.total_amount)}</Typography>
                    </Box>
                </CardContent>
            </Card>

            {order.status === 'confirmed' && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h5" mb={2}>
                            Separação e Expedição
                        </Typography>

                        {shipments.length === 0 ? (
                            <Typography color="text.secondary">Ainda não expedido.</Typography>
                        ) : (
                            shipments.map((shipment) => (
                                <Box key={shipment.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatDateTimeBR(shipment.shipped_at)} {shipment.carrier && `- ${shipment.carrier}`}
                                        {shipment.tracking_code && ` (rastreio: ${shipment.tracking_code})`}
                                    </Typography>
                                    <Table size="small" sx={{ mt: 1 }}>
                                        <TableBody>
                                            {shipment.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item.produto_variacao_nome}</TableCell>
                                                    <TableCell align="right">{item.quantidade}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ))
                        )}

                        <Stack direction="row" spacing={2}>
                            {order.fulfillment_status === 'reserved' && (
                                <Button variant="outlined" disabled={working} onClick={handleStartPicking}>
                                    Iniciar Separação
                                </Button>
                            )}
                            {(order.fulfillment_status === 'reserved' || order.fulfillment_status === 'picking') && (
                                <Button variant="contained" disabled={working} onClick={() => setShipDialogOpen(true)}>
                                    Expedir Pedido
                                </Button>
                            )}
                            {order.fulfillment_status === 'shipped' && !order.invoice && (
                                <Button variant="contained" disabled={working} onClick={handleInvoice}>
                                    Faturar
                                </Button>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => history.push('/dashboard/sales')}>Voltar</Button>

                {order.status === 'draft' && (
                    <Button color="error" variant="outlined" disabled={working} onClick={handleDelete}>
                        Excluir
                    </Button>
                )}

                {(order.status === 'draft' || (order.status === 'confirmed' && order.fulfillment_status !== 'shipped')) && (
                    <Button color="warning" variant="outlined" disabled={working} onClick={handleCancel}>
                        Cancelar Pedido
                    </Button>
                )}

                {order.status === 'draft' && (
                    <Button variant="contained" disabled={working} onClick={handleConfirm}>
                        Confirmar Pedido
                    </Button>
                )}
            </Box>

            <Dialog open={shipDialogOpen} onClose={() => setShipDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Expedir Pedido</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Transportadora (opcional)" fullWidth value={carrier} onChange={(e) => setCarrier(e.target.value)} />
                        <TextField
                            label="Código de rastreio (opcional)"
                            fullWidth
                            value={trackingCode}
                            onChange={(e) => setTrackingCode(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShipDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleShip} disabled={working}>
                        Confirmar Expedição
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewSalesOrder;
